import { useStates } from '@tiger/hooks';
import { Button } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { PlusCircleOutlined } from '@ant-design/icons';
import BlackListWarning from 'components/BlackListWarning';
import ErrorBox from 'components/ErrorBox';
import HashLink from 'components/HashLink';
import Loading from 'components/Loading';
import ScrollTop from 'components/ScrollTop';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import { useBlackWhiteStatus } from 'hooks/useBlackWhiteStatus';
import security from 'stores/security';
import { Source } from 'types/funding';
import API from 'utils/API';
import http from 'utils/http';
import AchEnabledBankCardList from './AchEnabledBankCardList';
import AchUnableBankCardList from './AchUnableBankCardList';
import './style.scss';

enum ACHStatus {
    initial,
    enabled,
    disabled
}

export interface ACHBankProps {
    source: Source;
    isIRA?: boolean;
}

interface ACHBankStates {
    modify: boolean; // 是否展示修改功能
    achStatus: ACHStatus;
    bankCardList: PlaidBankCardItem[];
    bankCardLoading: boolean;
    bankCardError: Error | null;
}

const ACHBank: React.FC<ACHBankProps> = observer(({ source, isIRA }) => {
    const [{ modify, achStatus, bankCardList, bankCardLoading, bankCardError }, setStates] = useStates<
        ACHBankStates,
        {}
    >({
        modify: false,
        achStatus: ACHStatus.initial,
        bankCardList: [],
        bankCardLoading: true,
        bankCardError: null
    });
    const { isLoading, error, data, mutate } = useBlackWhiteStatus(
        source === 'deposit' ? 'ACH_DEPOSIT' : 'ACH_WITHDRAW'
    );

    const getBankCardList = useCallback(async () => {
        setStates({
            bankCardLoading: true,
            bankCardError: null
        });

        try {
            const data = await http
                .get<{
                    items: PlaidBankCardItem[];
                }>(API.TRADE.funding.getCardList(), {
                    params: {
                        customer_id: security.currentCustomerId,
                        account_id: security.currentAccountId,
                        supported_bank: true // 是否支持从bank-card-relation获取银行账户 兼容旧版逻辑
                    }
                })
                .then(({ data }) => data);

            setStates({
                bankCardList: data.items
            });
        } catch (error) {
            setStates({
                bankCardError: error
            });
        }

        setStates({
            bankCardLoading: false
        });
    }, [setStates]);

    useEffect(() => {
        const achEnabledAction = () => {
            setStates({ achStatus: ACHStatus.enabled });

            getBankCardList();
        };

        if (security.isAccountOpened) {
            if (data) {
                if (!data.isBlack) {
                    achEnabledAction();
                } else {
                    setStates({ achStatus: ACHStatus.disabled, bankCardLoading: false });
                }
            }
        } else {
            achEnabledAction();
        }
    }, [data, setStates, getBankCardList]);

    useLayoutEffect(() => {
        if (bankCardList) {
            const enabledList = bankCardList.filter(({ status, id_source }) => {
                /**
                 * plaid银行卡全部展示
                 * 手动认证银行卡入金展示可用的、审核中；出金展示可用的
                 * */
                if (id_source === 'Bank') {
                    if (source === 'deposit') {
                        return ['AVAILABLE', 'VERIFIED'].includes(status);
                    }

                    return ['AVAILABLE'].includes(status);
                }

                return true;
            });

            // 默认没有可用的银行卡 展示编辑逻辑
            setStates({ modify: !enabledList?.length });
        }
    }, [bankCardList, source, setStates]);

    const handleManage = () => {
        setStates({
            modify: true
        });
    };

    const handleDone = () => {
        setStates({
            modify: false
        });
    };

    const handleDelete = () => {
        getBankCardList();
    };

    const render = () => {
        if (error && !data) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (isLoading || bankCardLoading) {
            return <Loading />;
        }

        if (bankCardError) {
            return <ErrorBox error={bankCardError} onClick={() => getBankCardList()} />;
        }

        if (achStatus === ACHStatus.disabled) {
            return <BlackListWarning source={source} type="ACH" />;
        }

        if (!bankCardList.length) {
            return (
                <Navigate
                    to="/profile/bankcards/auth/select-way"
                    state={{
                        source: source
                    }}
                    replace
                />
            );
        }

        return (
            <>
                <AchEnabledBankCardList
                    data={bankCardList}
                    source={source}
                    isModify={modify}
                    onDelete={handleDelete}
                    isIRA={isIRA}
                />
                {modify && <AchUnableBankCardList data={bankCardList} source={source} />}
                {!modify && (
                    <div className="add-bank">
                        <Link
                            to="/profile/bankcards/auth/select-way"
                            state={{
                                source: source,
                                list: bankCardList
                            }}>
                            <Button block className="ach-add-bank">
                                <PlusCircleOutlined className="add-icon" />
                                {__('Add a New Bank Account')}
                            </Button>
                        </Link>
                    </div>
                )}
                {!security.isMarscoAuditing && (
                    <div className="text-center">
                        {!modify ? (
                            <HashLink onClick={handleManage}>
                                {isIRA ? __('Manage Bank Profiles') : __('Manage Bank')}
                            </HashLink>
                        ) : (
                            <Button block type="primary" className="btn-important mt-3" onClick={handleDone}>
                                {__('Done')}
                            </Button>
                        )}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="ach-bank-root">
            <ScrollTop />
            {render()}
        </div>
    );
});

export default ACHBank;
